import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import ReferralRewardCard from "../components/app/ReferralRewardCard";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import {
  Download,
  CheckCircle,
  MapPin,
  Loader2,
  ArrowRight,
  FileText,
  Camera,
  Snowflake,
} from "lucide-react";

/**
 * SeasonalGuide (Autumn/Winter 2026-27) — iter211 Lead Magnet Landing Page
 *
 * Public, SEO-indexed at `/autumn-guide`. Captures an email in exchange for a
 * beautifully typeset PDF ("The Ultimate UK Autumn/Winter 2026-27 Campervan
 * Guide"). Successful capture:
 *   1. Rows into `db.lead_magnet_signups` (email + consent + UA).
 *   2. PDF emailed as attachment via Resend.
 *   3. Immediate direct-download fallback for slow / spam-filtered inboxes.
 *
 * The page is real, standalone content with 10 checklist items and 10 UK
 * regions rendered as HTML, so Google indexes it as a proper article. HowTo
 * + ItemList structured data ships in <head>.
 *
 * File kept named SpringGuide.jsx to preserve git history; the exported
 * component name and route slug both reflect the current season.
 */

const CHECKLIST = [
  { h: "Winterise the water system", b: "Drain fresh + waste tanks fully. Antifreeze in U-bends. One hard frost splits a boiler drain plug — £400 lesson if you skip it." },
  { h: "Swap butane for propane", b: "Butane stops gassing off below 5°C. Full propane 6kg bottle keeps the heater, hob and fridge running when nights drop." },
  { h: "Service the diesel heater / Truma", b: "Book a burner clean + glow-plug check now. Sooted-up heaters always trip at 3am on the first frost." },
  { h: "Thermal blinds + window insulation", b: "Reflectix or Silverscreens on every window cut condensation and drop overnight heat loss by up to 30%." },
  { h: "Tyres, wipers + winter screen wash", b: "Fresh wipers, tread check, and a -20°C screen wash mix. Supermarket summer-blue freezes on Highland A-roads by November." },
  { h: "Test the leisure battery + solar", b: "Solar drops to ~20% of summer output by December. Charge to 100% and check draw before your first cold night." },
  { h: "CO alarm + gas locker vents", b: "New CO batteries. Clear leaves off the low-level gas locker drop-vent. Autumn debris is a genuine killer." },
  { h: "Layers, down bag, hot-water bottle", b: "Merino + fleece + down puffy + waterproof shell. -10°C comfort sleeping bag (not extreme). One hot-water bottle changes everything." },
  { h: "Book hardstanding + full hookup", b: "Grass pitches waterlog from October. Filter CampCompass for hardstanding + EHU — often £16-22/night off-season." },
  { h: "Off-grid comms + offline maps", b: "Download offline OS Maps + your CampCompass shortlist. A Three or EE eSIM keeps signal alive in Snowdonia and the Dales." },
];

const REGIONS = [
  { name: "Northumberland Coast + Kielder", why: "Dark Sky Park — genuinely black skies for the aurora. Empty beaches in November are one of the UK's best-kept secrets." },
  { name: "Cairngorms + Aviemore", why: "Snow-camping proper. Aurora chances from October. Glenmore stays open all winter with hardstanding + hookup." },
  { name: "Lake District (Cumbria)", why: "Waterfalls at peak flow. Aira Force and Lodore Falls thunder. Skiddaw + Blencathra crowd-free." },
  { name: "Yorkshire Dales", why: "Misty mornings, log-fire snugs, Malham Cove without a queue. Hardstanding near Hawes stays open through winter." },
  { name: "Snowdonia / Eryri (North Wales)", why: "Dark Sky Reserve. Snow-topped Yr Wyddfa from a valley pitch. Beddgelert Forest has year-round hardstanding." },
  { name: "Peak District", why: "Gritstone edges silent. Under 3 hours from Manchester, Leeds and Birmingham for a proper mid-week reset." },
  { name: "Cornwall + North Devon", why: "Mild coastal winters. Cold-water surf at Fistral, then a wood-burner pitch a mile away." },
  { name: "New Forest (Hampshire)", why: "Ancient woodland turns copper. Ponies loose on the road. Sandy Balls + Ocknell keep hardstanding open." },
  { name: "Cotswolds", why: "Honey-stone villages framed by bare beech woods. Mild + rarely muddy. Pitches near Broadway under £20." },
  { name: "South Downs + Sussex Coast", why: "Chalk paths drain fast — best all-weather walking in England. Beachy Head at 4pm sunset is unforgettable." },
];

const DOWNLOAD_URL = "/api/lead-magnet/autumn-winter-2026.pdf";

export default function SeasonalGuide() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  // iter216 — pick up ?ref=xxx so the referrer's counter ticks on new signups.
  const [searchParams] = useSearchParams();
  const refCode = (searchParams.get("ref") || "").trim().toLowerCase();
  const [referral, setReferral] = useState(null);

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "The Ultimate UK Autumn/Winter 2026-27 Campervan Guide",
      description:
        "10-point cold-weather prep checklist + 10 winter-ready UK regions from the CampCompass community.",
      totalTime: "PT10M",
      step: CHECKLIST.map((c, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: c.h,
        text: c.b,
      })),
    }),
    []
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/lead-magnet/autumn-winter-2026/request", {
        email: email.trim(),
        consent: true,
        ref: refCode || undefined,
      });
      setSent(true);
      setReferral(data?.referral || null);
      toast.success("Guide sent — check your inbox");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col" data-testid="autumn-guide-page">
      <Header />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b hairline-strong bg-gradient-to-br from-[#8a2600] via-[#c23300] to-[#0e0e0e] text-white">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 80%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <span className="eyebrow !text-white/80 inline-flex items-center gap-1.5" data-testid="ag-eyebrow">
              <Snowflake className="w-3 h-3" /> Free PDF · Autumn/Winter 2026-27 · No card, no signup wall
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-3 mb-4">
              The Ultimate UK Autumn/Winter 2026-27
              <br />
              <span className="italic font-light opacity-90">Campervan Guide.</span>
            </h1>
            <p className="text-white/90 text-base lg:text-lg max-w-xl leading-relaxed">
              A 10-point cold-weather prep checklist, 10 winter-ready regions worth pointing the
              van at, and honest campsite picks — condensed into one shareable PDF you can print,
              forward or keep on your phone.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-white/90">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 flex-shrink-0" /> 4 pages, designed to print or scroll
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 flex-shrink-0" /> No watermark, share it freely
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 flex-shrink-0" /> Backed by 22,000+ UK campsites in
                the app
              </li>
            </ul>
          </div>

          {/* CAPTURE CARD */}
          <div className="lg:justify-self-end w-full lg:max-w-sm">
            <div className="bg-white text-[#0e0e0e] p-1 shadow-[0_10px_0_-2px_rgba(0,0,0,0.15)]">
              <div className="border hairline-strong p-6">
                <div className="eyebrow mb-2 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Get the PDF · 30 seconds
                </div>
                <h2 className="font-display text-2xl leading-tight mb-4">
                  Drop your email. We'll send it now.
                </h2>

                {sent ? (
                  <>
                    <div className="p-4 border-2 border-[#0a7f3f] bg-[#f4faf6] mb-4" data-testid="ag-sent">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#0a7f3f] flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-[#0e0e0e] leading-snug">
                          <div className="font-black uppercase tracking-wider text-[11px] text-[#0a7f3f] mb-1">
                            Sent
                          </div>
                          <p className="mb-2">
                            Check <b>{email}</b>. The PDF is attached, plus a link if the file gets
                            spam-filtered.
                          </p>
                          <a
                            href={DOWNLOAD_URL}
                            target="_blank"
                            rel="noopener"
                            className="inline-flex items-center gap-1.5 text-[#ff4500] font-bold text-xs uppercase tracking-widest underline"
                            data-testid="ag-download-now"
                          >
                            <Download className="w-3.5 h-3.5" /> Download it now
                          </a>
                        </div>
                      </div>
                    </div>
                    {/* iter216 — post-signup referral reward card */}
                    {referral && <ReferralRewardCard referral={referral} compact />}
                  </>
                ) : (
                  <form onSubmit={submit} className="space-y-3" data-testid="ag-form">
                    <input
                      type="email"
                      className="cc-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      required
                      data-testid="ag-email"
                    />
                    <button
                      type="submit"
                      disabled={busy}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#0e0e0e] text-white font-black text-xs uppercase tracking-widest px-4 py-3 shadow-[0_3px_0_0_#5a1500] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_#5a1500] transition-transform disabled:opacity-60"
                      data-testid="ag-submit"
                    >
                      {busy ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      Email me the guide
                    </button>
                    <p className="text-[10px] text-[#888] leading-tight">
                      One email now, occasional seasonal-tips updates. Unsubscribe anytime — we
                      hate spam too.
                    </p>
                  </form>
                )}

                <div className="mt-4 pt-4 border-t hairline">
                  <a
                    href={DOWNLOAD_URL}
                    target="_blank"
                    rel="noopener"
                    className="text-[11px] mono uppercase tracking-widest underline text-[#555]"
                    data-testid="ag-skip-download"
                  >
                    Or download without email <ArrowRight className="inline w-3 h-3 -mt-0.5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHECKLIST */}
      <section className="border-b hairline max-w-[1400px] mx-auto w-full px-6 lg:px-10 py-12 lg:py-16">
        <div className="mb-8">
          <div className="eyebrow mb-1.5">Part 1</div>
          <h2 className="font-display text-2xl md:text-3xl">10 things to sort before the first frost</h2>
        </div>
        <ol className="grid md:grid-cols-2 gap-x-8 gap-y-5">
          {CHECKLIST.map((c, i) => (
            <li key={i} className="flex gap-3" data-testid={`ag-check-${i + 1}`}>
              <span className="flex-shrink-0 w-7 h-7 bg-[#ff4500] text-white font-black text-xs flex items-center justify-center rounded-full mt-0.5">
                {i + 1}
              </span>
              <div>
                <div className="font-bold text-[15px] mb-0.5">{c.h}</div>
                <p className="text-sm text-[#555] leading-relaxed">{c.b}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* REGIONS */}
      <section className="border-b hairline max-w-[1400px] mx-auto w-full px-6 lg:px-10 py-12 lg:py-16">
        <div className="mb-8">
          <div className="eyebrow mb-1.5">Part 2</div>
          <h2 className="font-display text-2xl md:text-3xl">Where to point the van this winter</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
          {REGIONS.map((r, i) => (
            <div key={i} className="border-b hairline pb-4" data-testid={`ag-region-${i + 1}`}>
              <div className="flex items-baseline gap-2 mb-1">
                <MapPin className="w-3.5 h-3.5 text-[#ff4500] flex-shrink-0" />
                <div className="mono text-[10px] uppercase tracking-widest text-[#888]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-bold text-[15px]">{r.name}</div>
              </div>
              <p className="text-sm text-[#555] leading-relaxed ml-6">{r.why}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTAs */}
      <section className="max-w-[1400px] mx-auto w-full px-6 lg:px-10 py-14 lg:py-20">
        <div className="grid md:grid-cols-3 gap-4">
          <Link
            to="/browse"
            className="group p-6 border-2 border-[#0e0e0e] bg-white hover:bg-[#0e0e0e] hover:text-white transition-colors"
            data-testid="ag-cta-browse"
          >
            <MapPin className="w-6 h-6 mb-3" />
            <div className="eyebrow mb-1">Browse the app</div>
            <div className="font-display text-xl leading-tight mb-2">
              22,000+ UK sites with real filters
            </div>
            <div className="mono text-[11px] uppercase tracking-widest opacity-70">
              Open now <ArrowRight className="inline w-3 h-3 -mt-0.5" />
            </div>
          </Link>

          <Link
            to="/wall"
            className="group p-6 border-2 border-[#0e0e0e] bg-white hover:bg-[#0e0e0e] hover:text-white transition-colors"
            data-testid="ag-cta-wall"
          >
            <Camera className="w-6 h-6 mb-3" />
            <div className="eyebrow mb-1">Video reels + trip stories</div>
            <div className="font-display text-xl leading-tight mb-2">
              See the pitches before you book
            </div>
            <div className="mono text-[11px] uppercase tracking-widest opacity-70">
              Watch on the wall <ArrowRight className="inline w-3 h-3 -mt-0.5" />
            </div>
          </Link>

          <Link
            to="/share-your-trip"
            className="group p-6 border-2 border-[#ff4500] bg-[#ff4500] text-white hover:bg-[#e03d00] transition-colors"
            data-testid="ag-cta-share"
          >
            <FileText className="w-6 h-6 mb-3" />
            <div className="eyebrow !text-white/80 mb-1">Post your own</div>
            <div className="font-display text-xl leading-tight mb-2">
              Share this winter's first trip
            </div>
            <div className="mono text-[11px] uppercase tracking-widest opacity-80">
              1-click signup <ArrowRight className="inline w-3 h-3 -mt-0.5" />
            </div>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
